import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchCallLogs } from '../services/telephonyApi'
import { useTelephony } from '../context/TelephonyContext'
import { appToast } from '../../../components/AppToast'

// Imported Modular Components
import CallCenterHeader from './components/CallCenterHeader'
import SoftphoneDialer from './components/SoftphoneDialer'
import RecentCallHistoryCard from './components/RecentCallHistoryCard'
import CallbacksCard from './components/CallbacksCard'
import ManagerQueueMonitorCard from './components/ManagerQueueMonitorCard'

export default function CallCenterScreen() {
  const navigate = useNavigate()
  const {
    isSipDeviceOn,
    isSipToggling,
    sipError,
    handleSipToggle,
    makeCall,
    testRingtone,
    isAudioPrimed,
  } = useTelephony()

  // ── Dialer State ───────────────────────────────────────────────────────────
  const [dialedNumber, setDialedNumber] = useState('')
  const [selectedContact, setSelectedContact] = useState('')

  // ── Recent Call History State ──────────────────────────────────────────────
  const [recentLogs, setRecentLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)

  // Load recent 24h call logs — own calls only (Call Logs page shows all)
  const loadRecentLogs = useCallback(async () => {
    setLogsLoading(true)
    try {
      const res = await fetchCallLogs({ pageSize: 5, myCallsOnly: true })
      const rawLogs = res.data?.items || res.data || []
      const items = Array.isArray(rawLogs) ? rawLogs : []
      setRecentLogs(items.slice(0, 4))
    } catch (e) {
      console.warn('[CallCenter] Failed to load call logs:', e)
    } finally {
      setLogsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadRecentLogs()
  }, [loadRecentLogs])

  // Initiate Outbound WebRTC Call
  const handleInitiateCall = (phoneNumber) => {
    const numberToCall = phoneNumber || dialedNumber
    if (!numberToCall) {
      appToast.warning('Please enter or select a phone number first.')
      return
    }

    makeCall(
      numberToCall,
      selectedContact
        ? {
            name: selectedContact.name || selectedContact.label,
            project_name: selectedContact.project_name,
          }
        : {}
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-24">
      {/* ── Main Workstation Canopy Header ─────────────────────────────────── */}
      <CallCenterHeader
        isSipDeviceOn={isSipDeviceOn}
        isSipToggling={isSipToggling}
        sipError={sipError}
        onSipToggle={handleSipToggle}
        isAudioPrimed={isAudioPrimed}
        onTestRingtone={testRingtone}
      />

      {/* ── Main Grid Layout (Left Dialpad Panel + Right Content Panels) ────── */}
      <div className="grid grid-cols-12 gap-6">
        {/* ── LEFT PANEL: DIALPAD & CONTACT SEARCH (4 cols) (Modular) ────────── */}
        <SoftphoneDialer
          dialedNumber={dialedNumber}
          setDialedNumber={setDialedNumber}
          selectedContact={selectedContact}
          setSelectedContact={setSelectedContact}
          onInitiateCall={handleInitiateCall}
        />

        {/* ── RIGHT PANELS: RECENT CALL HISTORY, CALLBACKS & QUEUE MONITOR (8 cols) ────────── */}
        <div className="col-span-12 lg:col-span-8 space-y-6">
          {/* ── TOP ROW: SIDE-BY-SIDE CARDS (Recent Call History + My Callbacks) ── */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            {/* 1. Recent Call History Card (Modular) */}
            <RecentCallHistoryCard
              recentLogs={recentLogs}
              logsLoading={logsLoading}
              onViewFullHistory={() => navigate('/loop/call-logs')}
            />

            {/* 2. My Callbacks Card — handles its own API actions internally */}
            <CallbacksCard />
          </div>

          {/* ── BOTTOM ROW: MANAGER QUEUE MONITOR CARD (Modular) ── */}
          <ManagerQueueMonitorCard />
        </div>
      </div>
    </div>
  )
}
